import { useState, useCallback } from 'react';
import { apiRequest } from '../utils/auth';
import ImageLightbox from '../components/ImageLightbox';
import { FaEye, FaCheckCircle } from 'react-icons/fa';

interface OrphanFile {
  key: string;
  size: number;
  updated: string;
}

interface PrefixSummary {
  prefix: string;
  count: number;
  bytes: number;
}

interface ScanResult {
  scannedAt: string;
  totalObjects: number;
  referencedKeys: number;
  totalOrphans: number;
  totalOrphanBytes: number;
  filteredOut: number;
  byPrefix: PrefixSummary[];
  orphans: OrphanFile[];
  truncated: boolean;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

const formatDate = (s: string) =>
  new Date(s).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

// Top-level prefixes that map to known reference sources, for the prefix filter.
const PREFIX_OPTIONS = ['', 'generations/', 'extensions/', 'avatar/', 'articles/'];

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg|avif|heic)$/i;
const isPreviewable = (key: string) => IMAGE_EXT.test(key);

// `datetime-local` value (local, no tz) -> ISO string for the API.
const toIso = (local: string): string | undefined =>
  local ? new Date(local).toISOString() : undefined;

// Default the "created before" guard to 24h ago, as a `datetime-local` value.
const defaultBefore = (): string => {
  const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const inputStyle: React.CSSProperties = {
  padding: '7px 10px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px',
};
const labelStyle: React.CSSProperties = {
  color: 'var(--color-muted)', fontSize: '12px', display: 'block', marginBottom: '4px',
};
const thStyle: React.CSSProperties = {
  padding: '10px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px',
  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
};

export default function StoragePage() {
  const [prefix, setPrefix] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState(defaultBefore());

  const [result, setResult] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Lightbox preview state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState<string>('');
  const [previewLoadingKey, setPreviewLoadingKey] = useState<string | null>(null);

  const scan = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotice(null);
      setSelected(new Set());

      const params = new URLSearchParams({ limit: '2000' });
      if (prefix) params.set('prefix', prefix);
      const afterIso = toIso(createdAfter);
      const beforeIso = toIso(createdBefore);
      if (afterIso) params.set('createdAfter', afterIso);
      if (beforeIso) params.set('createdBefore', beforeIso);

      const res = await apiRequest<{ success: boolean } & ScanResult>(
        `/api/admin/storage/orphans?${params}`,
      );
      setResult(res);
    } catch (err: any) {
      setError(err.message || 'Failed to scan storage');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [prefix, createdAfter, createdBefore]);

  const openPreview = useCallback(async (key: string) => {
    try {
      setPreviewLoadingKey(key);
      setError(null);
      const res = await apiRequest<{ success: boolean; url: string }>(
        `/api/admin/storage/preview-url?key=${encodeURIComponent(key)}`,
      );
      setPreviewKey(key);
      setPreviewUrl(res.url);
    } catch (err: any) {
      setError(err.message || 'Failed to load preview');
    } finally {
      setPreviewLoadingKey(null);
    }
  }, []);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (!result) return;
    setSelected((prev) =>
      prev.size === result.orphans.length
        ? new Set()
        : new Set(result.orphans.map((o) => o.key)),
    );
  };

  const selectedBytes = result
    ? result.orphans.filter((o) => selected.has(o.key)).reduce((sum, o) => sum + o.size, 0)
    : 0;

  const deleteSelected = useCallback(async () => {
    if (selected.size === 0) return;
    const confirmed = window.confirm(
      `Permanently delete ${selected.size} file(s) (${formatBytes(selectedBytes)}) from the bucket?\n\n` +
      `Files that have become referenced since the scan will be skipped automatically. This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      setError(null);
      setNotice(null);
      const keys = Array.from(selected);
      const res = await apiRequest<{
        success: boolean;
        deletedCount: number;
        skippedCount: number;
        skipped: string[];
      }>('/api/admin/storage/orphans/delete', {
        method: 'POST',
        body: JSON.stringify({ keys }),
      });

      setNotice(
        `Deleted ${res.deletedCount} file(s)` +
        (res.skippedCount > 0 ? `, skipped ${res.skippedCount} now-referenced file(s).` : '.'),
      );

      // Remove the deleted keys from the current view.
      const deletedKeys = new Set(keys.filter((k) => !res.skipped.includes(k)));
      setResult((prev) =>
        prev
          ? {
              ...prev,
              orphans: prev.orphans.filter((o) => !deletedKeys.has(o.key)),
              totalOrphans: prev.totalOrphans - deletedKeys.size,
            }
          : prev,
      );
      setSelected(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to delete files');
    } finally {
      setDeleting(false);
    }
  }, [selected, selectedBytes]);

  return (
    <div className="page">
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ color: 'var(--color-text)', margin: 0, fontSize: '24px' }}>Storage Garbage Collection</h1>
        <p style={{ color: 'var(--color-muted)', marginTop: '4px', fontSize: '14px' }}>
          Find bucket files no longer referenced by any generation, lens, avatar, or article — then delete them.
          Lens-creation previews (accepted or cancelled) stop counting as references after 24h; their files are
          collectible because accepted samples are saved as copies under extensions/.
        </p>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px',
        padding: '16px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap',
        gap: '12px', alignItems: 'flex-end',
      }}>
        <div>
          <label style={labelStyle}>Prefix</label>
          <select value={prefix} onChange={(e) => setPrefix(e.target.value)} style={inputStyle}>
            {PREFIX_OPTIONS.map((p) => (
              <option key={p} value={p}>{p === '' ? 'All folders' : p}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Created after</label>
          <input
            type="datetime-local"
            value={createdAfter}
            onChange={(e) => setCreatedAfter(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Created before</label>
          <input
            type="datetime-local"
            value={createdBefore}
            onChange={(e) => setCreatedBefore(e.target.value)}
            style={inputStyle}
          />
        </div>

        <button
          onClick={scan}
          disabled={loading}
          style={{
            padding: '7px 16px', backgroundColor: 'var(--color-accent-strong)', color: 'white', border: 'none',
            borderRadius: '6px', cursor: loading ? 'default' : 'pointer', fontSize: '14px',
            fontWeight: 500, opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Scanning…' : 'Scan'}
        </button>

        <p style={{ color: 'var(--color-subtle)', fontSize: '12px', margin: 0, maxWidth: '280px' }}>
          Tip: keep a "created before" of ~24h ago so in-flight uploads aren't flagged.
        </p>
      </div>

      {notice && (
        <div style={{ color: 'var(--color-success)', backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-success-strong)', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
          {notice}
        </div>
      )}
      {error && (
        <div style={{ color: 'var(--color-danger)', backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-danger-strong)', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {result && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Objects scanned', value: result.totalObjects.toLocaleString() },
              { label: 'Referenced keys', value: result.referencedKeys.toLocaleString() },
              { label: 'Orphans', value: result.totalOrphans.toLocaleString(), highlight: true },
              { label: 'Reclaimable', value: formatBytes(result.totalOrphanBytes), highlight: true },
              { label: 'Outside time window', value: result.filteredOut.toLocaleString() },
            ].map((c) => (
              <div key={c.label} style={{
                backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px',
                padding: '14px 18px', minWidth: '140px',
              }}>
                <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginBottom: '4px' }}>{c.label}</div>
                <div style={{ color: c.highlight ? 'var(--color-warning)' : 'var(--color-text)', fontSize: '20px', fontWeight: 600 }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Per-prefix breakdown */}
          {result.byPrefix.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--color-text)', fontSize: '15px', marginBottom: '8px' }}>Orphans by folder</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {result.byPrefix.map((p) => (
                  <span key={p.prefix} style={{
                    backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border)', borderRadius: '6px',
                    padding: '6px 12px', color: 'var(--color-text)', fontSize: '13px',
                  }}>
                    <strong>{p.prefix}/</strong> — {p.count.toLocaleString()} files, {formatBytes(p.bytes)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <button
              onClick={deleteSelected}
              disabled={selected.size === 0 || deleting}
              style={{
                padding: '7px 16px',
                backgroundColor: selected.size === 0 ? 'var(--color-surface-hover)' : 'var(--color-danger-strong)',
                color: selected.size === 0 ? 'var(--color-subtle)' : 'white',
                border: selected.size === 0 ? '1px solid var(--color-border)' : 'none',
                borderRadius: '6px',
                cursor: selected.size === 0 || deleting ? 'default' : 'pointer',
                fontSize: '14px', fontWeight: 500,
              }}
            >
              {deleting ? 'Deleting…' : `Delete selected (${selected.size})`}
            </button>
            {selected.size > 0 && (
              <span style={{ color: 'var(--color-muted)', fontSize: '13px' }}>{formatBytes(selectedBytes)} selected</span>
            )}
            {result.truncated && (
              <span style={{ color: 'var(--color-warning-text)', fontSize: '13px', marginLeft: 'auto' }}>
                Showing first {result.orphans.length.toLocaleString()} of {result.totalOrphans.toLocaleString()} orphans — narrow the filters to see the rest.
              </span>
            )}
          </div>

          {/* Orphans table */}
          <div style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
            {result.orphans.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>
                <FaCheckCircle style={{ color: 'var(--color-success)', verticalAlign: 'middle', marginRight: '6px' }} />
                No orphaned files in this window.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-surface-hover)', borderBottom: '2px solid var(--color-border)' }}>
                    <th style={{ ...thStyle, width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selected.size === result.orphans.length && result.orphans.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th style={thStyle}>Key</th>
                    <th style={{ ...thStyle, width: '120px' }}>Size</th>
                    <th style={{ ...thStyle, width: '180px' }}>Last modified</th>
                    <th style={{ ...thStyle, width: '110px' }}>Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {result.orphans.map((o) => (
                    <tr
                      key={o.key}
                      onClick={() => toggle(o.key)}
                      style={{
                        borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
                        backgroundColor: selected.has(o.key) ? 'var(--color-surface-hover)' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '10px 16px' }}>
                        <input type="checkbox" checked={selected.has(o.key)} onChange={() => toggle(o.key)} onClick={(e) => e.stopPropagation()} />
                      </td>
                      <td style={{ padding: '10px 16px', color: 'var(--color-text)', fontSize: '13px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{o.key}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--color-muted)', fontSize: '13px', whiteSpace: 'nowrap' }}>{formatBytes(o.size)}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--color-muted)', fontSize: '13px', whiteSpace: 'nowrap' }}>{formatDate(o.updated)}</td>
                      <td style={{ padding: '10px 16px' }}>
                        {isPreviewable(o.key) ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); openPreview(o.key); }}
                            disabled={previewLoadingKey === o.key}
                            style={{
                              padding: '5px 12px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)',
                              border: '1px solid var(--color-border)', borderRadius: '6px',
                              cursor: previewLoadingKey === o.key ? 'default' : 'pointer', fontSize: '13px',
                            }}
                          >
                            {previewLoadingKey === o.key ? 'Loading…' : <><FaEye style={{ verticalAlign: 'middle', marginRight: '4px' }} /> View</>}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--color-subtle)', fontSize: '13px' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!result && !loading && (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>
          Choose a time window and click <strong>Scan</strong> to find orphaned files.
        </div>
      )}

      <ImageLightbox
        isOpen={previewUrl !== null}
        imageUrl={previewUrl || ''}
        altText={previewKey}
        onClose={() => { setPreviewUrl(null); setPreviewKey(''); }}
      />
    </div>
  );
}
